// The word-cloud inputs of an open-ended block, ported from the coffee block's calcWcProps.
//
// The cloud sizes itself to the box it is drawn in unless the mounting view fixes its dimensions,
// so the width and the height it derives are the cloud's own to work out. `changed` tells the cloud
// its own stored config no longer matches the live one - the param store lags the page-model
// attribute.
//
// The corpus a family draws from is `calcRawData`: the base one reads the answer's cloud words,
// while a family with nested answers (combo) derives it from its scope.

import {
  getColorThemes,
  getCustomPalettes,
  getFeatures,
  getModelValue,
  getStoredReportParams,
  getWcCustomPresets,
  isGuest
} from '@aytm/app/stats/lib/stat-page'

// what the cloud needs to know it may offer, beyond the block's own components
const CLOUD_FEATURES = [
  'word_clouds',
  'oe_tags_management',
  'oe_tags_view',
  'oe_sentiments_management',
  'oe_sentiments_view'
]

export const calcRawData = ({ answer } = {}) => answer?.cloudWords || { words: [] }

export const calcCloudFeatures = () => {
  const features = getFeatures()

  return Object.fromEntries(CLOUD_FEATURES.map(token => [token, features[token]]))
}

export const calcWcProps = ({
  questionId,
  questionKey,
  format,
  answer,
  answerVariants,
  components,
  config,
  dimensions,
  disabled,
  isVerbatimType,
  rawData,
  handlers = {}
} = {}) => {
  const param = `wc${questionId}`

  return {
    globalChartMode: getModelValue('chartMode'),
    model: {
      id: questionId,
      key: questionKey,
      useRawData: format?.useRawData || false,
      items: answerVariants || null,
      changed: (getModelValue(param) || null) !== (getStoredReportParams()[param] || null)
    },
    data: {
      colorThemes: getColorThemes(),
      rawData,
      customPalettes: getCustomPalettes(),
      customPresets: getWcCustomPresets()
    },
    components,
    features: calcCloudFeatures(),
    width: dimensions?.width || null,
    height: dimensions?.height || null,
    config: structuredClone(config || {}),
    isVerbatimType,
    isSurveyGuest: isGuest(),
    isLoading: 'loading' === answer,
    disabled: disabled || false,
    onParamsChange: handlers.onParamsChange,
    onMaskUploaded: handlers.onMaskUploaded,
    // a cloud of answer options has no verbatims to narrow to, so a word takes no click: one would only
    // write an active word the cloud never reads into the report's saved settings
    onWordClick: format?.useRawData ? undefined : handlers.onWordClick,
    onReset: handlers.onReset,
    onSave: handlers.onSave,
    onApplyToAll: handlers.onApplyToAll,
    onExport: handlers.onExport
  }
}
